import React from "react";
import { Button } from "antd";
import styled from "@emotion/styled";

interface QuantityControlProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  backgroundColor?: string;
  onMaxQuantity?: () => void;
}

const QuantityControl: React.FC<QuantityControlProps> = ({
  value,
  onChange,
  min = 0,
  max = Infinity,
  backgroundColor = "transparent",
  onMaxQuantity,
}) => {
  const handleDecrease = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const handleIncrease = () => {
    if (value < max) {
      onChange(value + 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value === "" ? 0 : parseInt(e.target.value);

    // 유효한 숫자가 아니면 리턴
    if (isNaN(newValue)) return;

    // 최대값 초과시
    if (newValue > max) {
      onMaxQuantity?.();
      onChange(max);
      return;
    }

    // 음수 입력 방지
    if (newValue < min) {
      onChange(min);
      return;
    }

    onChange(newValue);
  };
  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // 입력 필드 클릭 시 전체 텍스트 선택
    e.currentTarget.select();
  };

  return (
    <ControlWrapper backgroundColor={backgroundColor}>
      <StyledButton onClick={handleDecrease} disabled={value <= min}>
        -
      </StyledButton>
      <QuantityDisplay backgroundColor={backgroundColor}>
        <QuantityInput
          type="number"
          value={value === 0 ? "0" : value}
          onChange={handleInputChange}
          onClick={handleInputClick}
          backgroundColor={backgroundColor}
          min={min}
          max={max}
        />

        {/* {value} */}
      </QuantityDisplay>
      <StyledButton onClick={handleIncrease} disabled={value === max}>
        +
      </StyledButton>
    </ControlWrapper>
  );
};

const QuantityInput = styled.input<{ backgroundColor: string }>(
  ({ theme, backgroundColor }) => ({
    flex: 1,
    height: "100%",
    border: "none",
    textAlign: "center",
    borderLeft: `1px solid ${theme.color.border_gray}`,
    borderRight: `1px solid ${theme.color.border_gray}`,
    backgroundColor,
    width: "100%",
    padding: 0,
    outline: "none",
    // 숫자 입력 화살표 제거
    "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  }),
);

const ControlWrapper = styled.div<{ backgroundColor: string }>(
  ({ theme, backgroundColor }) => ({
    width: "8.8rem",
    height: "2.4rem",
    display: "flex",
    alignItems: "center",
    boxShadow: "none !important",
    border: `1px solid ${theme.color.border_gray}`,
    backgroundColor,

    "&& .ant-btn-default:disabled": {
      backgroundColor: "transparent",
      boxShadow: "none !important",
    },
    "&.ant-btn-default:active": {
      boxShadow: "none !important",
    },
    "&:active": {
      boxShadow: "none !important",
    },
    "&:hover": {
      boxShadow: "none !important",
      backgroundColor: "transparent",
    },
  }),
);

const StyledButton = styled(Button)(({ theme }) => ({
  width: "2.4rem",
  height: "2.4rem",
  borderRadius: "0",
  backgroundColor: "transparent",
  boxShadow: "none !important",
  border: "none",
  padding: "0",

  "&:hover": {
    boxShadow: "none !important",
    backgroundColor: "transparent !important",
    border: "none",
  },
  "&:active": {
    boxShadow: "none !important",
    backgroundColor: "transparent !important",
    border: "none",
  },
}));

const QuantityDisplay = styled.div<{ backgroundColor: string }>(
  ({ theme, backgroundColor }) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    height: "100%",
    borderLeft: `1px solid ${theme.color.border_gray}`,
    borderRight: `1px solid ${theme.color.border_gray}`,
    backgroundColor,
  }),
);

export default QuantityControl;
